import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { PlayerManager } from './components/PlayerManager';
import { TournamentFees } from './components/TournamentFees';
import { TransportFees } from './components/TransportFees';
import { FoodMiscFees } from './components/FoodMiscFees';
import { Admin } from './components/Admin';
import { AdminLogin } from './components/AdminLogin';
import { SimpleDrag } from './components/SimpleDrag';
import { useLocalStorage } from './hooks/useLocalStorage';
import { usePlayers, usePayments, useExpenses, useConfig } from './hooks/useDatabase';
import { Player, Payment, Expense, AppConfiguration } from './types';

const initialPlayers: Player[] = [
  { id: '1', name: 'Kamal', isActive: true },
  { id: '2', name: 'Sailesh', isActive: true },
  { id: '3', name: 'Nikhil', isActive: true },
  { id: '4', name: 'Anna', isActive: true },
  { id: '5', name: 'Anjeev', isActive: true },
  { id: '6', name: 'Gullu', isActive: true },
  { id: '7', name: 'Shyam', isActive: true },
  { id: '8', name: 'Nitish', isActive: true },
  { id: '9', name: 'Harry', isActive: true },
  { id: '10', name: 'Amit', isActive: true },
  { id: '11', name: 'Kandwil', isActive: true },
];

const initialConfig: AppConfiguration = {
  tournamentFee: 16000,
  transportTotal: 31000,
  teamName: 'MICC DDN'
};

function Navigation() {
  return (
    <nav className="bg-white shadow-lg mb-6 rounded-lg border border-gray-200">
      <div className="px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🏏</span>
              <span className="font-bold text-lg text-green-700">Cricket Tracker</span>
            </div>
            <div className="flex space-x-4">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium text-black"
              >
                Dashboard
              </Link>
              <Link
                to="/admin"
                className="px-4 py-2 text-sm font-medium text-black"
              >
                Admin
              </Link>
            </div>
          </div>
          <div className="text-2xl text-gray-800 font-bold">
            🏆 Dehradun Cricket Trip
          </div>
        </div>
      </div>
    </nav>
  );
}

function Dashboard({
  players,
  payments,
  expenses,
  config,
  onUpdatePayments,
  onUpdateExpenses
}: {
  players: Player[];
  payments: Payment[];
  expenses: Expense[];
  config: AppConfiguration;
  onUpdatePayments: (payments: Payment[]) => void;
  onUpdateExpenses: (expenses: Expense[]) => void;
}) {
  return (
    <>
      <PlayerManager
        players={players}
        payments={payments}
        expenses={expenses}
        config={config}
      />

      <TournamentFees
        players={players}
        payments={payments}
        config={config}
        onUpdatePayments={onUpdatePayments}
      />

      <TransportFees
        players={players}
        payments={payments}
        config={config}
        onUpdatePayments={onUpdatePayments}
      />

      <FoodMiscFees
        players={players}
        expenses={expenses}
        payments={payments}
        onUpdateExpenses={onUpdateExpenses}
        onUpdatePayments={onUpdatePayments}
      />

      <div className="mt-8 bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-green-800 flex items-center gap-2">
          🏆 Tournament Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
            <div className="text-3xl mb-2">👥</div>
            <div className="text-3xl font-bold text-blue-700">
              {players.filter(p => p.isActive).length}
            </div>
            <div className="text-sm font-medium text-blue-600">Squad Players</div>
          </div>
          <div className="text-center bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
            <div className="text-3xl mb-2">💰</div>
            <div className="text-3xl font-bold text-green-700">
              ₹{payments.filter(p => p.isPaid).reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
            </div>
            <div className="text-sm font-medium text-green-600">Total Collected</div>
          </div>
          <div className="text-center bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
            <div className="text-3xl mb-2">🧾</div>
            <div className="text-3xl font-bold text-purple-700">
              ₹{expenses.reduce((sum, e) => sum + e.amount, 0).toLocaleString()}
            </div>
            <div className="text-sm font-medium text-purple-600">Trip Expenses</div>
          </div>
          <div className="text-center bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
            <div className="text-3xl mb-2">⏳</div>
            <div className="text-3xl font-bold text-orange-700">
              ₹{(() => {
                const activePlayerCount = players.filter(p => p.isActive).length;
                const activePlayers = players.filter(p => p.isActive);
                const tournamentPerHead = config.tournamentFee;
                const transportTotal = config.transportTotal;
                const transportPerHead = activePlayerCount > 0 ? Math.round(transportTotal / activePlayerCount) : 0;

                // Calculate food per head based on actual expenses (Splitwise style)
                const foodExpenses = expenses?.filter(e => e.categoryId === 'food') || [];
                const totalFoodExpenses = foodExpenses.reduce((sum, e) => sum + e.amount, 0);
                const foodPerHead = activePlayerCount > 0 ? Math.round(totalFoodExpenses / activePlayerCount) : 0;

                // Calculate total outstanding for all players combined (Splitwise style)
                let totalOutstanding = 0;

                activePlayers.forEach(player => {
                  // Calculate what each player owes per category

                  // Tournament: what they owe vs what they paid
                  const tournamentOwed = tournamentPerHead;
                  const tournamentPaid = payments
                    .filter(p => p.playerId === player.id && p.categoryId === 'tournament' && p.isPaid)
                    .reduce((sum, p) => sum + p.amount, 0);
                  const tournamentBalance = tournamentOwed - tournamentPaid;

                  // Transport: what they owe vs what they paid
                  const transportOwed = transportPerHead;
                  const transportPaid = payments
                    .filter(p => p.playerId === player.id && p.categoryId === 'transport' && p.isPaid)
                    .reduce((sum, p) => sum + p.amount, 0);
                  const transportBalance = transportOwed - transportPaid;

                  // Food/Misc: what they owe vs what they spent (Splitwise logic)
                  const foodOwed = foodPerHead;
                  const foodSpent = foodExpenses
                    .filter(e => e.paidBy === player.id)
                    .reduce((sum, e) => sum + e.amount, 0);
                  const foodBalance = foodOwed - foodSpent;

                  // Total balance for this player (positive = owes money, negative = is owed money)
                  const playerTotalBalance = tournamentBalance + transportBalance + foodBalance;

                  // Only add positive balances to outstanding (people who owe money)
                  if (playerTotalBalance > 0) {
                    totalOutstanding += playerTotalBalance;
                  }
                });

                return totalOutstanding;
              })().toLocaleString()}
            </div>
            <div className="text-sm font-medium text-orange-600">Outstanding</div>
          </div>
        </div>
      </div>
    </>
  );
}

function App() {
  // Environment detection: Use database if Supabase env vars are available, otherwise localStorage
  const hasSupabaseConfig = !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY);

  // Use database if we have Supabase config
  const useDatabase = hasSupabaseConfig;

  // Database hooks (always called, but will handle env vars internally)
  const [dbPlayers, setDbPlayers, dbPlayersLoading, dbPlayersError, refreshDbPlayers] = usePlayers();
  const [dbPayments, setDbPayments, dbPaymentsLoading, dbPaymentsError, refreshDbPayments] = usePayments();
  const [dbExpenses, setDbExpenses, dbExpensesLoading, dbExpensesError, refreshDbExpenses] = useExpenses();
  const [dbConfig, setDbConfig, dbConfigLoading, dbConfigError, refreshDbConfig] = useConfig();

  // localStorage hooks (fallback)
  const [lsPlayers, setLsPlayers] = useLocalStorage<Player[]>('cricket-trip-players', initialPlayers);
  const [lsPayments, setLsPayments] = useLocalStorage<Payment[]>('cricket-trip-payments', []);
  const [lsExpenses, setLsExpenses] = useLocalStorage<Expense[]>('cricket-trip-expenses', []);
  const [lsConfig, setLsConfig] = useLocalStorage<AppConfiguration>('cricket-trip-config', initialConfig);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useLocalStorage<boolean>('admin-authenticated', false);

  // Choose data source based on configuration
  const players = useDatabase ? (dbPlayers || []) : lsPlayers;
  const setPlayers = useDatabase ? setDbPlayers : setLsPlayers;
  const payments = useDatabase ? (dbPayments || []) : lsPayments;
  const setPayments = useDatabase ? setDbPayments : setLsPayments;
  const expenses = useDatabase ? (dbExpenses || []) : lsExpenses;
  const setExpenses = useDatabase ? setDbExpenses : setLsExpenses;
  const config = useDatabase ? (dbConfig || initialConfig) : lsConfig;
  const setConfig = useDatabase ? setDbConfig : setLsConfig;

  // Aggregate errors for database status
  const hasDbErrors = !!(dbPlayersError || dbPaymentsError || dbExpensesError || dbConfigError);
  const isDbLoading = dbPlayersLoading || dbPaymentsLoading || dbExpensesLoading || dbConfigLoading;

  const handleSyncData = async () => {
    if (useDatabase) {
      try {
        console.log('Syncing database data...');
        await Promise.all([
          refreshDbPlayers(),
          refreshDbPayments(),
          refreshDbExpenses(),
          refreshDbConfig()
        ]);
        console.log('Database sync completed');
      } catch (error) {
        console.error('Database sync failed:', error);
      }
    } else {
      console.log('Using localStorage mode - no database sync needed');
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-8 py-8 max-w-6xl">
          <Navigation />

          <Routes>
            <Route
              path="/"
              element={
                <Dashboard
                  players={players}
                  payments={payments}
                  expenses={expenses}
                  config={config}
                  onUpdatePayments={setPayments}
                  onUpdateExpenses={setExpenses}
                />
              }
            />
            <Route
              path="/admin"
              element={
                isAdminAuthenticated ? (
                  <div>
                    <div className="flex justify-between items-center mb-6 bg-white rounded-lg p-4 shadow-lg border border-gray-200">
                      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        🏏 Admin Panel
                      </h1>
                      <button
                        onClick={() => setIsAdminAuthenticated(false)}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm shadow-md"
                      >
                        Logout
                      </button>
                    </div>
                    <Admin
                      players={players}
                      config={config}
                      onUpdatePlayers={setPlayers}
                      onUpdateConfig={setConfig}
                    />
                  </div>
                ) : (
                  <AdminLogin onLogin={setIsAdminAuthenticated} />
                )
              }
            />
          </Routes>

          {/* Database Status Modal */}
          <SimpleDrag
            useDatabase={useDatabase}
            hasSupabaseConfig={hasSupabaseConfig}
            isLoading={isDbLoading}
            hasErrors={hasDbErrors}
            playersError={dbPlayersError}
            paymentsError={dbPaymentsError}
            expensesError={dbExpensesError}
            configError={dbConfigError}
            onSyncData={handleSyncData}
          />
        </div>
      </div>
    </Router>
  );
}

export default App;